import { computed, DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface ProcessUpdateLiveStats {
    totalToday: number;
    updatesPerMinute: number;
    distinctSourcesToday: number;
    lastReceivedAtUtc: string | null;
    topSources: { source: string; count: number }[];
}

export interface StatsTimePoint {
    time: string;           // HH:mm:ss label
    updatesPerMinute: number;
    totalToday: number;
    distinctSources: number;
}

/** Max data points kept in the rolling time-series buffer */
const MAX_HISTORY = 60;

/** Interval (ms) for the idle-tick that decays updatesPerMinute when no data arrives */
const IDLE_TICK_INTERVAL = 5000;

/**
 * Manages a SignalR connection to the process-updates hub.
 * Handles token acquisition (MSAL / offline), auto-reconnect, and
 * exposes reactive signals for connection state and live statistics.
 */
@Injectable({ providedIn: 'root' })
export class SignalRService {
    private readonly msalService = inject(MsalService);
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    private connection: signalR.HubConnection | null = null;
    private idleTimer: any = null;
    private lastServerUpdateAt = 0;

    /** Current connection state */
    readonly connectionState = signal<ConnectionState>('disconnected');

    /** Latest live stats received from the hub */
    readonly liveStats = signal<ProcessUpdateLiveStats | null>(null);

    /** Rolling time-series history (max MAX_HISTORY points) */
    readonly statsHistory = signal<StatsTimePoint[]>([]);

    /** Chart labels derived from history */
    readonly chartLabels = computed(() => this.statsHistory().map(p => p.time));

    /** Last error message (cleared on successful reconnect) */
    readonly lastError = signal<string | null>(null);

    /**
     * Start the SignalR connection. Safe to call multiple times —
     * subsequent calls are ignored if already connected or connecting.
     */
    async start(): Promise<void> {
        if (this.connection) {
            return; // already initialised
        }

        const isOffline = !!(environment as any).offline;

        const hubUrl = `${environment.apiRootUrl}/hubs/process-updates`;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => this.getAccessToken(isOffline),
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // Exponential backoff: 0s, 2s, 4s, 8s, 16s, then cap at 30s
                    const delay = Math.min(2000 * Math.pow(2, retryContext.previousRetryCount), 30000);
                    return delay;
                },
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // Wire up lifecycle events (run inside Angular zone for change detection)
        this.connection.onreconnecting((error) => {
            this.zone.run(() => {
                this.connectionState.set('reconnecting');
                this.lastError.set(error?.message ?? 'Connection lost, reconnecting…');
            });
        });

        this.connection.onreconnected(() => {
            this.zone.run(() => {
                this.connectionState.set('connected');
                this.lastError.set(null);
            });
        });

        this.connection.onclose((error) => {
            this.zone.run(() => {
                this.connectionState.set('disconnected');
                if (error) {
                    this.lastError.set(error.message);
                }
            });
        });

        // Register the server → client method
        this.connection.on('ReceiveProcessUpdateStats', (stats: ProcessUpdateLiveStats) => {
            this.zone.run(() => {
                this.lastServerUpdateAt = Date.now();
                this.liveStats.set(stats);
                this.lastError.set(null);

                // Append to time-series buffer
                const now = new Date();
                const timeLabel = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
                const point: StatsTimePoint = {
                    time: timeLabel,
                    updatesPerMinute: stats.updatesPerMinute,
                    totalToday: stats.totalToday,
                    distinctSources: stats.distinctSourcesToday,
                };
                const history = [...this.statsHistory(), point];
                this.statsHistory.set(history.length > MAX_HISTORY ? history.slice(-MAX_HISTORY) : history);
            });
        });

        // Start idle-tick timer: if no server update in IDLE_TICK_INTERVAL, push a 0 throughput point
        this.idleTimer = setInterval(() => {
            const elapsed = Date.now() - this.lastServerUpdateAt;
            if (elapsed >= IDLE_TICK_INTERVAL && this.liveStats()) {
                this.zone.run(() => {
                    const currentStats = this.liveStats()!;
                    // Decay updatesPerMinute to 0
                    this.liveStats.set({ ...currentStats, updatesPerMinute: 0 });

                    // Add a zero-throughput point to the chart
                    const now = new Date();
                    const timeLabel = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
                    const point: StatsTimePoint = {
                        time: timeLabel,
                        updatesPerMinute: 0,
                        totalToday: currentStats.totalToday,
                        distinctSources: currentStats.distinctSourcesToday,
                    };
                    const history = [...this.statsHistory(), point];
                    this.statsHistory.set(history.length > MAX_HISTORY ? history.slice(-MAX_HISTORY) : history);
                });
            }
        }, IDLE_TICK_INTERVAL);

        // Cleanup on destroy
        this.destroyRef.onDestroy(() => {
            this.stop();
        });

        // Start the connection with retry
        await this.startWithRetry();
    }

    /** Stop the connection gracefully. */
    async stop(): Promise<void> {
        if (this.idleTimer) {
            clearInterval(this.idleTimer);
            this.idleTimer = null;
        }
        if (this.connection) {
            try {
                await this.connection.stop();
            } catch {
                // Best-effort stop; ignore errors during teardown
            }
            this.connection = null;
            this.connectionState.set('disconnected');
        }
    }

    private async startWithRetry(maxRetries = 5): Promise<void> {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                this.connectionState.set('connecting');
                await this.connection!.start();
                this.connectionState.set('connected');
                this.lastError.set(null);
                return;
            } catch (err: any) {
                const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
                this.lastError.set(`Connection failed (attempt ${attempt + 1}/${maxRetries}): ${err?.message ?? err}`);

                if (attempt < maxRetries - 1) {
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    this.connectionState.set('disconnected');
                }
            }
        }
    }

    private async getAccessToken(isOffline: boolean): Promise<string> {
        if (isOffline) {
            return 'offline-dev-token';
        }

        try {
            const result = await this.msalService.acquireTokenSilent({
                scopes: environment.apiConfig.scopes,
            }).toPromise();
            return result?.accessToken ?? '';
        } catch (err) {
            console.error('[SignalR] Failed to acquire access token', err);
            return '';
        }
    }
}
